import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from "react-router-dom";

import T, { Translate } from '../i18n/i18n';
import { PlayersList, Title, Section, Separator, Player, PlayerPhoto, PlayerName, PlayerAdd, PlayerEmailInput, PlayerEmailSend, TableCode, PlayerRemove, Button, ButtonRefresh, NoParticipants } from './PlayersList.styles';
import { loadPlayers , addPlayers, removePlayers, getAwaitingPlayers, allowAwaitingPlayer, denyAwaitingPlayer } from '../../services/players';
import { playersActions, chatActions, authActions, rtcBufferActions } from '../../redux/actions/';

export default ({tableId}) => {

    const [email, setEmail] = useState('');
    const [selectedPlayer, setSelectedPlayer] = useState(null);
    const [tableCode, setTableCode] = useState('');
    const [awaiting, setAwaiting] = useState([]);

    const owner = useSelector(state => state.playersState.owner);
    const participants = useSelector(state => state.playersState.participants);
    const online = useSelector(state => state.playersState.online);
    const userEmail = useSelector(state => state.authState.email);
    const isOwner = useSelector(state => state.authState.isOwner);
    const playerId = useSelector(state => state.authState.id);
    const videoId = useSelector(state => state.jukeboxState.playing);
    const prevOnline = useRef([]);

    const history = useHistory();
    const dispatch = useDispatch();

    const refreshAwaiting = useCallback(async () => {
        if (isOwner) {
            let players = await getAwaitingPlayers(tableId);
            setAwaiting(players.payload || []);
        }
    }, [tableId, isOwner]);

    useEffect(() => {
        refreshAwaiting();
    }, [refreshAwaiting])

    useEffect(() => {
        if (owner) {
            dispatch(authActions.setOwnership(userEmail === owner.email));
        }
    }, [userEmail, owner, dispatch]);

    useEffect(() => {
        let players = [owner, ...participants];
    
        if (!players || players[0] === null) {
            return;
        }

        let inPlayers = players.some(p => p.email === userEmail);
        if (!inPlayers) {
            history.replace('/home');
        }
    }, [owner, participants, history, userEmail]);

    useEffect(() => {
        
        let enter = online.filter(e => prevOnline.current.indexOf(e) < 0);
        let leave = prevOnline.current.filter(e => online.indexOf(e) < 0);
        
        if (enter.length > 0) {
            if (prevOnline.current.length > 0) {
                enter.map(u => dispatch(chatActions.addMessage({
                    message: {command: 'notification-enter', content: u.user}
                })));
            }
        } 
        
        if (leave.length > 0){
            leave.map(u => dispatch(chatActions.addMessage({
                message: {command: 'notification-leave', content: u.user}
            })));
        }

        if (isOwner) {
            (async () => {
                let players = await loadPlayers(tableId);
                dispatch(playersActions.loadPlayers(players.owner || null, players.players || []));
                if (videoId) {
                    dispatch(rtcBufferActions.pushOutputBuffer({
                        type: 'jukeboxPlayYT',
                        data: {videoId}
                    }));
                }
            })();
        }

        prevOnline.current = online;
    }, [online, isOwner, tableId, dispatch]);

    useEffect(() => {
        (async () => {
            let players = await loadPlayers(tableId);
            dispatch(playersActions.loadPlayers(players.owner || null, players.players || []));
            setTableCode(players.code);
        })();
    }, [dispatch, tableId]);

    const submitEmail = async () => {
        if (email && email.length > 0) {
            let player = await addPlayers(tableId, email);
            if (player.email) {
                dispatch(playersActions.addPlayers(player));
                dispatch(rtcBufferActions.pushOutputBuffer({
                    type: 'playersSync',
                    data: [...participants, player]
                }));
                refreshAwaiting();
            }
            setEmail('');
        }
    }

    const handleKeypress = (e) => {
        if (e.which === 13) {
            submitEmail();
        }
    }

    const revokePlayer = async () => {
        const newPlayerList = await removePlayers(tableId, selectedPlayer);

        if (newPlayerList) {
            dispatch(playersActions.loadPlayers(owner, newPlayerList));
            dispatch(rtcBufferActions.pushOutputBuffer({
                type: 'playersSync',
                data: newPlayerList
            }));
        }
    }

    const toggleSelectedPlayer = (playerId) => {
        setSelectedPlayer(pId => playerId === pId ? null : playerId);
    }

    const handleAllowAccess = async (player) => {
        let players = await allowAwaitingPlayer(tableId, player._id);
        setAwaiting(players.payload || []);
        dispatch(playersActions.addPlayers(player));
        refreshAwaiting();
    }

    const handleDenyAccess = async (player) => {
        let players = await denyAwaitingPlayer(tableId, player._id);
        setAwaiting(players.payload || []);
        refreshAwaiting();
    }

    return <PlayersList>
        {isOwner && <>
            <Section><T value="players.addByEmail" /></Section>
            <PlayerAdd>
                <PlayerEmailInput value={email} placeholder={Translate('players.email')} onChange={(e)=> {setEmail(e.target.value)}} onKeyPress={handleKeypress}/>
                <PlayerEmailSend onClick={() => {submitEmail()}} />
                <Separator><T value="players.or" /></Separator>
                <TableCode readOnly={true} value={tableCode} />
                <ButtonRefresh color="#f92" onClick={() => {refreshAwaiting()}} />
            </PlayerAdd>
        </>}
        <Title><T value="players.gm" /></Title>
        { owner && <Player className={online.some(o => o.email === owner.email) ? 'online' : ''}>
            <PlayerPhoto color={owner.color}>{owner.name.substr(0,1)}</PlayerPhoto>
            <PlayerName color={owner.color}>{owner.name}</PlayerName>
        </Player>}
        {(awaiting && awaiting.length > 0) ? <>
            <Title><T value="players.waiting" /></Title>
            {awaiting.map((player, index) => <Player key={index} className="online">
                <PlayerPhoto color={player.color}>{player.name.substr(0,1)}</PlayerPhoto>
                <PlayerName color={player.color}>{player.name}</PlayerName>
                <Button color="#0c0" onClick={()=>{handleAllowAccess(player)}}><T value="players.allowaccess" /></Button>
                <Button color="#f44" onClick={()=>{handleDenyAccess(player)}}><T value="players.blockaccess" /></Button>
            </Player>)}
        </> : null}
        <Title><T value="players.players" /></Title>
        {!participants || participants.length < 1 ? <NoParticipants>
                <T value="players.noParticipants" />
            </NoParticipants> : participants.map((player, index) => <Player key={index} className={(selectedPlayer === player._id ? 'selected' : '') + (online.some(o => o.email === player.email) ? ' online' : '')} onClick={(e) => {toggleSelectedPlayer(player._id)}}>
                <PlayerPhoto color={player.color}>{player.name.substr(0,1)}</PlayerPhoto>
                <PlayerName color={player.color}>{player.name}</PlayerName>
                {(isOwner || selectedPlayer === playerId) ? <PlayerRemove className={'removePlayer'} onClick={(e) => {revokePlayer()}}/> : null}
            </Player>)
        }
    </PlayersList>
};